import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { User } from '../types/auth';
import { colocService, Coloc, getSelectedColoc, setSelectedColoc } from '../services/coloc.service';
import AccessBanner from './AccessBanner';
import { useCapabilities } from '../contexts/CapabilitiesContext';
import '../styles/Layout.css';
import './CapabilityGate.css';

interface LayoutProps {
  children: React.ReactNode;
}

/**
 * Le menu du propriétaire, en TABLE plutôt qu'en JSX répété.
 *
 * Il comptait douze entrées dans un seul groupe « Colocation », mêlant quatre
 * métiers — le bien, la mise en location, les occupants, la comptabilité. Le
 * défaut est devenu criant en ouvrant les capacités hors offre : quatre entrées
 * jusque-là masquées sont apparues d'un coup.
 *
 * Deux niveaux de lecture sont conservés, et ils ne disent pas la même chose :
 *   - les groupes ci-dessous sont CONTEXTUELS — ils suivent le sélecteur de
 *     colocation en tête de barre ;
 *   - `MENU_TRANSVERSE` ne bascule jamais, d'où son titre explicite : rien à
 *     l'écran ne signalait cette différence auparavant.
 *
 * `cap` absente = entrée toujours visible. Sinon l'entrée est masquée si la
 * capacité est « unavailable », et porte un verrou si elle est « unlockable ».
 */
type EntreeMenu = { id: string; label: string; cap?: string };
type GroupeMenu = { titre: string | null; items: EntreeMenu[] };

const MENU_COLOCATION: GroupeMenu[] = [
  // Sans titre : c'est la porte d'entrée, pas une catégorie.
  { titre: null, items: [{ id: 'dashboard', label: 'Tableau de bord' }] },
  {
    titre: 'Le bien',
    items: [
      { id: 'details', label: 'Détails' },
      { id: 'rooms', label: 'Chambres' },
    ],
  },
  {
    // Ordonné comme le cycle réel : on attire, on reçoit, on loge.
    titre: 'Location',
    items: [
      { id: 'statistiques', label: 'Audience' },
      { id: 'contacts', label: 'Contacts', cap: 'contacts' },
      { id: 'candidats', label: 'Candidats', cap: 'candidates' },
      { id: 'tenants', label: 'Locataires' },
    ],
  },
  {
    // Ordonné comme le flux : on dépose, on corrige, on consulte, on répartit.
    titre: 'Dépenses',
    items: [
      { id: 'drop-invoices', label: 'Déposer des factures', cap: 'invoices' },
      { id: 'pending-invoices', label: 'Factures en attente', cap: 'invoices' },
      { id: 'invoices', label: 'Factures', cap: 'invoices' },
      { id: 'charges', label: 'Charges', cap: 'charges' },
      { id: 'logs', label: 'Journaux', cap: 'logs' },
    ],
  },
];

/** Ne suit PAS le sélecteur de colocation — d'où le titre, qui fait le travail
 *  que « Gestion » ne faisait pas. */
const MENU_TRANSVERSE: GroupeMenu[] = [
  {
    titre: 'Toutes colocations',
    items: [
      { id: 'to-assign', label: 'À assigner', cap: 'invoices' },
      { id: 'my-providers', label: 'Mes fournisseurs', cap: 'providers' },
    ],
  },
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [colocs, setColocs] = useState<Coloc[]>([]);
  const [selectedColoc, setCurrentColoc] = useState<Coloc | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const isAgency = authService.isAgency();
  // Catalogue de capacités (saas/12) : « unavailable » = masqué (aucun plan ne
  // le vend), « unlockable » = visible avec un verrou (l'écran verrouillé est
  // l'argumentaire). Aucune liste en dur : tout vient de l'API.
  const { stateOf } = useCapabilities();
  const isHidden = (cap: string) => stateOf(cap) === 'unavailable';
  const lockOf = (cap: string) =>
    stateOf(cap) === 'unlockable' ? <span className="menu-lock" aria-label="Fonctionnalité payante">🔒</span> : null;

  /** Rend un groupe, ou rien si toutes ses entrées sont hors offre. */
  const rendreGroupe = (groupe: GroupeMenu, cle: string) => {
    const visibles = groupe.items.filter((e) => !e.cap || !isHidden(e.cap));
    if (visibles.length === 0) return null;
    return (
      <React.Fragment key={cle}>
        {groupe.titre && (
          <>
            <div className="menu-separator"></div>
            <div className="menu-section-label">{groupe.titre}</div>
          </>
        )}
        <ul className="menu">
          {visibles.map((e) => (
            <li key={e.id} className={activeMenuItem === e.id ? 'active' : ''}>
              <button className="menu-button" onClick={() => handleMenuClick(e.id)}>
                {e.label}
                {e.cap ? lockOf(e.cap) : null}
              </button>
            </li>
          ))}
        </ul>
      </React.Fragment>
    );
  };

  // Determine active menu item based on current path
  const getActiveMenuItem = () => {
    const path = location.pathname;
    if (path.includes('/drop-invoices')) return 'drop-invoices';
    if (path.includes('/pending-invoices')) return 'pending-invoices';
    if (path.includes('/to-assign')) return 'to-assign';
    if (path.includes('/logs')) return 'logs';
    if (path.includes('/charges')) return 'charges';
    if (path.includes('/statistiques')) return 'statistiques';
    if (path.includes('/rooms')) return 'rooms';
    if (path.includes('/candidats')) return 'candidats';
    if (path.includes('/agency/documents')) return 'agency-documents';
    if (path.includes('/contacts')) return 'contacts';
    if (path.includes('/tenants')) return 'tenants';
    if (path.includes('/invoices')) return 'invoices';
    if (path.includes('/my-providers')) return 'my-providers';
    if (path.includes('/agences')) return 'agences';
    if (path.includes('/settings')) return 'settings';
    if (path.includes('/my-account')) return 'my-account';
    if (path.includes('/users')) return 'users';
    if (path.includes('/details')) return 'details';
    return 'dashboard';
  };
  
  const [activeMenuItem, setActiveMenuItem] = useState<string>(getActiveMenuItem());
  
  useEffect(() => {
    const loadUserAndColocs = async () => {
      setIsLoading(true);
      try {
        const currentUser = authService.getCurrentUser();
        if (!currentUser) {
          navigate('/signin');
          return;
        }
        setUser(currentUser.user);

        // Load colocs. Agency : périmètre scoped porté par le token (pas d'appel
        // /colocs owner). Owner : liste complète via l'API.
        const userColocs = authService.isAgency()
          ? ((currentUser.user.colocs || []) as Coloc[])
          : await colocService.getColocs();
        setColocs(userColocs);
        
        // Get selected coloc from storage or set the first one as default
        const storedColoc = getSelectedColoc();
        if (storedColoc) {
          // Verify the stored coloc exists in the user's colocs
          const exists = userColocs.some(c => c.id === storedColoc.id);
          if (exists) {
            setCurrentColoc(storedColoc);
          } else if (userColocs.length > 0) {
            // If stored coloc doesn't exist, use the first one
            setCurrentColoc(userColocs[0]);
            setSelectedColoc(userColocs[0]);
          } else {
            // Stored coloc no longer exists and no colocs available — clear stale data
            localStorage.removeItem("selected_coloc");
          }
        } else if (userColocs.length > 0) {
          // No stored coloc, use the first one
          setCurrentColoc(userColocs[0]);
          setSelectedColoc(userColocs[0]);
        }
      } catch {
        // silent
      } finally {
        setIsLoading(false);
      }
    };
    
    loadUserAndColocs();
    
    // Update active menu item when location changes
    setActiveMenuItem(getActiveMenuItem());
  }, [navigate, location]);

  const handleLogout = () => {
    authService.logout();
    navigate('/signin');
  };

  const handleMenuClick = (menuItem: string) => {
    setActiveMenuItem(menuItem);

    // Navigate to the appropriate route
    switch (menuItem) {
      case 'dashboard':
        navigate('/dashboard');
        break;
      case 'invoices':
        navigate('/invoices');
        break;
      case 'pending-invoices':
        navigate('/pending-invoices');
        break;
      case 'drop-invoices':
        navigate('/drop-invoices');
        break;
      case 'charges':
        navigate('/charges');
        break;
      case 'logs':
        navigate('/logs');
        break;
      case 'details':
        navigate('/details');
        break;
      case 'to-assign':
        navigate('/to-assign');
        break;
      case 'my-providers':
        navigate('/my-providers');
        break;
      case 'tenants':
        navigate('/tenants');
        break;
      case 'statistiques':
        navigate('/statistiques');
        break;
      case 'rooms':
        navigate('/rooms');
        break;
      case 'candidats':
        navigate(isAgency ? '/agency/candidats' : '/candidats');
        break;
      case 'contacts':
        navigate(isAgency ? '/agency/contacts' : '/contacts');
        break;
      case 'agency-documents':
        navigate('/agency/documents');
        break;
      case 'users':
        navigate('/users');
        break;
      case 'agences':
        navigate('/agences');
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'my-account':
        navigate('/my-account');
        break;
      default:
        navigate('/dashboard');
    }
  };
  
  const handleColocChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const colocId = e.target.value;
    const coloc = colocs.find(c => c.id === colocId);
    if (coloc) {
      setCurrentColoc(coloc);
      setSelectedColoc(coloc);

      // Navigate to the role's home when coloc is changed
      navigate(isAgency ? '/agency/candidats' : '/dashboard');
    }
  };

  if (isLoading) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div className="layout-container">
      {/* Premier élément atteint au clavier : saute l'en-tête et le menu. */}
      <a href="#contenu" className="lien-evitement">Aller au contenu</a>
      <AccessBanner />
      <header className="layout-header">
        <h1>Coloc App</h1>
        <div className="user-info">
          <span>{user?.firstName} {user?.lastName}</span>
          <button onClick={handleLogout} className="logout-button">
            Déconnexion
          </button>
        </div>
      </header>
      
      <div className="layout-content">
        <nav className="sidebar">
          {isAgency ? (
            <>
              <div className="menu-section-label">Colocation</div>
              <div className="sidebar-coloc-selector">
                <select
                  value={selectedColoc?.id || ''}
                  onChange={handleColocChange}
                  className="coloc-dropdown sidebar-coloc-dropdown"
                >
                  {colocs.map(coloc => (
                    <option key={coloc.id} value={coloc.id}>
                      {coloc.name}
                    </option>
                  ))}
                </select>
              </div>
              <ul className="menu">
                <li className={activeMenuItem === 'candidats' ? 'active' : ''}>
                  <button
                    className="menu-button"
                    onClick={() => handleMenuClick('candidats')}
                  >
                    Candidats
                  </button>
                </li>
                <li className={activeMenuItem === 'contacts' ? 'active' : ''}>
                  <button
                    className="menu-button"
                    onClick={() => handleMenuClick('contacts')}
                  >
                    Contacts
                  </button>
                </li>
                <li className={activeMenuItem === 'agency-documents' ? 'active' : ''}>
                  <button
                    className="menu-button"
                    onClick={() => handleMenuClick('agency-documents')}
                  >
                    Déposer un document
                  </button>
                </li>
              </ul>
            </>
          ) : (
          <>
          {colocs.length > 0 ? (
            <>
              <div className="menu-section-label">Colocation</div>
              <div className="sidebar-coloc-selector">
                <select
                  value={selectedColoc?.id || ''}
                  onChange={handleColocChange}
                  className="coloc-dropdown sidebar-coloc-dropdown"
                >
                  {colocs.map(coloc => (
                    <option key={coloc.id} value={coloc.id}>
                      {coloc.name}
                    </option>
                  ))}
                </select>
              </div>
              {MENU_COLOCATION.map((g, n) => rendreGroupe(g, `coloc-${n}`))}
              {MENU_TRANSVERSE.map((g, n) => rendreGroupe(g, `transverse-${n}`))}
            </>
          ) : (
            <ul className="menu">
              <li>
                <button
                  className="menu-button"
                  onClick={() => navigate('/create-coloc')}
                  style={{ fontWeight: 'bold' }}
                >
                  + Créer une colocation
                </button>
              </li>
            </ul>
          )}
          <div className="menu-separator"></div>
          <div className="menu-section-label">Mon compte</div>
          <ul className="menu">
            {/* Partager une colocation avec un tiers est une décision de
                compte, pas un détail de la page Contacts — où la gestion des
                accès était jusqu'ici cachée derrière « Emails en copie ». */}
            <li className={activeMenuItem === 'agences' ? 'active' : ''}>
              <button
                className="menu-button"
                onClick={() => handleMenuClick('agences')}
              >
                Agences{lockOf('agency_access')}
              </button>
            </li>
            <li className={activeMenuItem === 'settings' ? 'active' : ''}>
              <button
                className="menu-button"
                onClick={() => handleMenuClick('settings')}
              >
                Paramètres{lockOf('settings')}
              </button>
            </li>
            <li className={activeMenuItem === 'my-account' ? 'active' : ''}>
              <button
                className="menu-button"
                onClick={() => handleMenuClick('my-account')}
              >
                Mon compte
              </button>
            </li>
          </ul>
          </>
          )}
        </nav>
        
        <main id="contenu" className="main-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
